import { useEffect, useRef, useState } from "react";
import { PROGRESS_TYPES } from "./goalTypes";
import type { GoalCreate, GoalLevel, ProgressType } from "../types";

interface AddGoalDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (payload: GoalCreate) => void;
  level: GoalLevel;
  periodKey: string;
  /** "тиждень 32 · 2026" — so you can see where the goal is about to land. */
  periodText: string;
}

/**
 * Creating a goal: name it, say how it gets ticked off, done.
 *
 * The type picker is the reason this is a dialog rather than the one-line form
 * it replaces. Four ways to finish a goal cannot be inferred from a title, and
 * guessing them from whether a number was typed — which is what the old form
 * did — is how the previous app ended up with year-level checklists nobody
 * could ever tick.
 *
 * Everything but the title is optional and the target only appears for the one
 * type that has one, so the fast path stays: click, type, Enter.
 */
export default function AddGoalDialog({
  open,
  onClose,
  onCreate,
  level,
  periodKey,
  periodText,
}: AddGoalDialogProps) {
  const [title, setTitle] = useState("");
  const [type, setType] = useState<ProgressType>("checkbox");
  const [target, setTarget] = useState("");
  const [unit, setUnit] = useState("");
  const [steps, setSteps] = useState<string[]>([]);
  const [step, setStep] = useState("");
  const titleRef = useRef<HTMLInputElement>(null);

  // Reset on the way in, not on the way out: clearing the fields as the dialog
  // fades would show the user their own text being wiped.
  useEffect(() => {
    if (!open) return;
    setTitle("");
    setType("checkbox");
    setTarget("");
    setUnit("");
    setSteps([]);
    setStep("");
    titleRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const clean = title.trim();
    if (!clean) return;
    const parsed = Number(target.replace(",", "."));
    // A step still sitting in the field when «Додати» is pressed was meant to
    // be in the list; dropping it silently would lose the last thing typed.
    const pending = step.trim();
    const allSteps = pending ? [...steps, pending] : steps;
    onCreate({
      level,
      period_key: periodKey,
      title: clean,
      progress_type: type,
      // The server clamps the rest: only a number goal has a target of its own.
      target_val: type === "number" && Number.isFinite(parsed) && parsed > 0 ? parsed : 0,
      unit: type === "number" ? unit.trim() : "",
      steps: type === "checklist" ? allSteps : [],
    });
  };

  const addStep = () => {
    const clean = step.trim();
    if (!clean) return;
    setSteps((current) => [...current, clean]);
    setStep("");
  };

  return (
    <div
      inert={!open}
      className={`fixed inset-0 z-50 grid place-items-center p-4 ${
        open ? "" : "pointer-events-none"
      }`}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Закрити"
        onClick={onClose}
        className={`absolute inset-0 h-full w-full bg-bg/70 backdrop-blur-sm transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label="Нова ціль"
        className={`jv-elevated relative flex w-full max-w-md flex-col gap-4 p-4 shadow-3 transition-[opacity,transform] duration-200 ease-drawer sm:p-5 ${
          open ? "scale-100 opacity-100" : "scale-95 opacity-0"
        }`}
      >
        <div className="flex items-baseline gap-2">
          <h2 className="text-sm font-semibold text-fg">Нова ціль</h2>
          <span className="truncate text-micro text-muted">{periodText}</span>
        </div>

        <input
          ref={titleRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          placeholder="Що зробити?"
          aria-label="Назва цілі"
          className="jv-input w-full min-w-0 text-sm"
        />

        <fieldset className="min-w-0">
          <legend className="mb-2 text-micro text-muted">Як відмічати</legend>
          <div className="grid grid-cols-2 gap-2">
            {PROGRESS_TYPES.map((option) => {
              const active = type === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setType(option.key)}
                  aria-pressed={active}
                  className={`flex items-center gap-2.5 rounded-card border p-2.5 text-left transition-colors ${
                    active
                      ? "border-accent bg-accent-soft text-fg"
                      : "border-line bg-surface-2 text-muted hover:border-accent/50 hover:text-fg"
                  }`}
                >
                  <option.Icon
                    className={`size-[18px] shrink-0 ${active ? "text-accent" : ""}`}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-medium">{option.label}</span>
                    <span className="block truncate text-micro text-muted">{option.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {type === "number" && (
          <div className="flex gap-2">
            <input
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              inputMode="decimal"
              placeholder="Скільки"
              aria-label="Числова ціль"
              className="jv-input min-w-0 flex-1 text-sm"
            />
            <input
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              maxLength={20}
              placeholder="од."
              aria-label="Одиниця виміру"
              className="jv-input w-24 text-sm"
            />
          </div>
        )}

        {type === "checklist" && (
          <div className="flex flex-col gap-2">
            {steps.length > 0 && (
              <ul className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {steps.map((text, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-muted" />
                    <span className="min-w-0 flex-1 text-xs break-words text-fg">{text}</span>
                    <button
                      type="button"
                      onClick={() => setSteps((current) => current.filter((_, i) => i !== index))}
                      aria-label={`Видалити пункт «${text}»`}
                      className="shrink-0 px-1 text-xs text-muted transition-colors hover:text-danger"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <input
              value={step}
              onChange={(e) => setStep(e.target.value)}
              onKeyDown={(e) => {
                // Enter adds the step and keeps the cursor here, so a whole
                // list is typed without the mouse. On an empty field it falls
                // through to the form and creates the goal.
                if (e.key === "Enter" && step.trim()) {
                  e.preventDefault();
                  addStep();
                }
              }}
              maxLength={200}
              placeholder="+ пункт"
              aria-label="Новий пункт"
              className="jv-input w-full text-sm"
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="jv-btn">
            Скасувати
          </button>
          <button type="submit" className="jv-btn-primary" disabled={!title.trim()}>
            Додати
          </button>
        </div>
      </form>
    </div>
  );
}
